import type { ComponentProps } from "react"
import { useMemo, useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Placeholder from "@/v2-ui/Placeholder"
import Button from "@/v2-backoffice-client/components/button"
import UptimeConfigCategoryDialog from "@/v2-backoffice-client/uptime/configSheet/UptimeConfigCategoryDialog"
import UptimeConfigCategoryRow from "@/v2-backoffice-client/uptime/configSheet/UptimeConfigCategoryRow"
import type { UptimeConfigQuery } from "@/v2-backoffice-client/uptime/config/__types__/uptime.config.query"

type Platform = UptimeConfigQuery["getStatusConfig"]["Platforms"][number]
type Category = UptimeConfigQuery["getStatusConfig"]["Categories"][number]

export type UptimeConfigCategoriesTabProps = ComponentProps<"div"> & {
  platforms: Platform[]
  categories: Category[]
}

// UptimeConfigCategoriesTab groups categories by their owning
// platform so the operator can scan platform-by-platform without
// the cognitive overhead of reading PlatformId on every row. The
// "Shared" bucket at the bottom catches IsSharedAcrossPlatforms
// rows that intentionally float across multiple platforms (e.g.
// the Cloud Provider category that the v2 + propcloud + newsec
// cards all reference).
function UptimeConfigCategoriesTab(props: UptimeConfigCategoriesTabProps) {
  const { platforms, categories, className, ...divProps } = props
  const [editing, setEditing] = useState<Category | null>(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const platformsById = useMemo(() => {
    const map = new Map<string, Platform>()
    for (const p of platforms) map.set(p.Id, p)
    return map
  }, [platforms])

  // Bucket categories by platform for rendering. Shared categories
  // collect under a synthetic "Shared across platforms" group so
  // they're not invisible to the operator browsing one platform.
  const groups = useMemo(() => {
    const byPlatform = new Map<string, Category[]>()
    const shared: Category[] = []
    for (const c of categories) {
      if (c.IsSharedAcrossPlatforms) shared.push(c)
      const list = byPlatform.get(c.PlatformId) ?? []
      list.push(c)
      byPlatform.set(c.PlatformId, list)
    }
    const out: { id: string; label: string; items: Category[] }[] = []
    const sortedPlatforms = [...platforms].toSorted(
      (a, b) => a.Priority - b.Priority || a.Name.localeCompare(b.Name),
    )
    for (const p of sortedPlatforms) {
      const items = byPlatform.get(p.Id) ?? []
      if (items.length === 0) continue
      out.push({
        id: p.Id,
        label: p.Name,
        items: [...items].toSorted(
          (a, b) => a.Priority - b.Priority || a.Name.localeCompare(b.Name),
        ),
      })
    }
    if (shared.length > 0) {
      out.push({
        id: "__shared__",
        label: "Shared across platforms",
        items: [...shared].toSorted(
          (a, b) => a.Priority - b.Priority || a.Name.localeCompare(b.Name),
        ),
      })
    }
    return out
  }, [categories, platforms])

  return (
    <div className={cn("uptime-config-tab", className)} {...divProps}>
      <div className="uptime-config-tab-header">
        <div className="uptime-config-tab-header-text">
          <h3 className="uptime-config-tab-title">Categories</h3>
          <p className="uptime-config-tab-description">
            Sub-cards inside each platform. The Sources list is what links a
            category to incoming probe rows.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconSrc="plus"
          onClick={() => {
            setIsCreateOpen(true)
          }}
        >
          New category
        </Button>
      </div>

      {groups.length === 0 ? (
        <Placeholder className="py-8">
          No categories yet. Create one and link it to one or more probe
          sources.
        </Placeholder>
      ) : (
        <div className="uptime-config-groups">
          {groups.map((g) => (
            <section key={g.id} className="uptime-config-group">
              <h4 className="uptime-config-group-title">{g.label}</h4>
              <ul className="uptime-config-list">
                {g.items.map((c) => (
                  <UptimeConfigCategoryRow
                    key={c.Id}
                    category={c}
                    platformName={platformsById.get(c.PlatformId)?.Name}
                    onEdit={() => {
                      setEditing(c)
                    }}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <UptimeConfigCategoryDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        mode="create"
        platforms={platforms}
      />
      <UptimeConfigCategoryDialog
        open={editing !== null}
        onOpenChange={(o) => {
          if (!o) setEditing(null)
        }}
        mode="edit"
        platforms={platforms}
        category={editing}
      />
    </div>
  )
}

export default UptimeConfigCategoriesTab
